import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { api } from "../lib/api";
import useSEO from "../hooks/useSEO";
import { FadeUp, SectionTag } from "../components/motion";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  useSEO(
    project ? `${project.name} — ${project.client_sector} Project | SGSNet Patna` : "Case Study | SGSNet Patna",
    project ? `${project.objective || ""} Delivered by SATI Global Systems (SGSNet), Patna.` : ""
  );

  useEffect(() => {
    api.get(`/projects/${slug}`)
      .then((res) => setProject(res.data))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <main className="pt-32 pb-24 max-w-7xl mx-auto px-8"><div className="h-96 rounded-2xl bg-panel animate-pulse" data-testid="project-detail-loading" /></main>;
  }
  if (!project) {
    return (
      <main className="pt-32 pb-24 text-center" data-testid="project-not-found">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Case study not found</h1>
        <Link to="/projects" className="mt-4 inline-block text-blue-600 text-sm">← All projects</Link>
      </main>
    );
  }

  const sections = [
    ["Project Objective", project.objective],
    ["SGS Scope", project.scope],
    ["Implementation Challenges", project.challenges],
    ["Solution", project.solution],
    ["Outcome", project.outcome],
  ];

  return (
    <main data-testid={`project-detail-${slug}`} className="pb-24">
      <section className="relative pt-16 sm:pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={project.image} alt={project.name} className="w-full h-full object-cover spotlight-img opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFFFFF]/70 via-[#FFFFFF]/85 to-[#FFFFFF]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <Link to="/projects" data-testid="project-back-link"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-slate-500 hover:text-blue-600 transition-colors">
              <ArrowLeft size={13} /> Projects
            </Link>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-8">
              <SectionTag>{project.client_sector}</SectionTag>
              <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight max-w-3xl">
                {project.name}
              </h1>
              <p className="mt-4 flex items-center gap-2 text-sm text-slate-600">
                <MapPin size={14} className="text-blue-600" /> {project.location}
              </p>
            </div>
          </FadeUp>
          {project.metrics?.length > 0 && (
            <FadeUp delay={0.2}>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl">
                {project.metrics.map((m, i) => (
                  <div key={i} className="p-4 rounded-xl bg-[#F1F5F9]/80 border border-slate-200 backdrop-blur" data-testid={`project-metric-${i}`}>
                    <div className="font-heading text-2xl font-bold text-blue-600">{m.value}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1">{m.label}</div>
                  </div>
                ))}
              </div>
            </FadeUp>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-10">
          {sections.map(([title, text], i) => (
            text ? (
              <FadeUp key={title} delay={i * 0.06}>
                <div data-testid={`case-section-${i}`}>
                  <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-blue-600">{title}</h2>
                  <p className="mt-3 text-slate-700 leading-relaxed">{text}</p>
                </div>
              </FadeUp>
            ) : null
          ))}
        </div>
        <div className="lg:col-span-4">
          <FadeUp delay={0.1}>
            <div className="sticky top-24 bg-panel border border-slate-200 rounded-2xl p-6">
              <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-blue-600">Key Technologies</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {(project.technologies || []).map((t) => (
                  <span key={t} className="px-3 py-1.5 rounded-md bg-blue-50/40 border border-blue-200/50 text-xs text-blue-700">{t}</span>
                ))}
              </div>
              <Link to="/contact" data-testid="project-cta"
                className="group mt-8 flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full bg-blue-600 text-[#FFFFFF] font-semibold text-sm hover:bg-blue-700 transition-colors">
                Start a Similar Project <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
